import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Thermometer, Shield, TrendingUp, Droplets, CheckCircle2, Award } from 'lucide-react';

const benefits = [
  {
    icon: Thermometer,
    title: 'Heat Reflective',
    value: '40%',
    description: 'Reduce roof heat absorption by up to 40% with our cool roof coatings',
    color: '#F95738'
  },
  {
    icon: Shield,
    title: 'Durability',
    value: '15+ Years',
    description: 'Premium quality paints backed by our industry-leading warranty',
    color: '#38BDF8'
  },
  {
    icon: TrendingUp,
    title: 'Property Value',
    value: '+12%',
    description: 'A freshly painted roof can increase your property value significantly',
    color: '#FBBF24'
  },
  {
    icon: Droplets,
    title: 'Weather Sealed',
    value: '100%',
    description: 'Complete protection against harsh Australian weather conditions',
    color: '#22C55E'
  }
];

const features = [
  'Professional pressure cleaning',
  'Mould & fungus treatment',
  'Crack repair & re-pointing',
  'Premium Dulux paints',
  'Full re-bedding if needed',
  'Complete cleanup included'
];

export default function BetterRoofSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="py-20 md:py-32 bg-[#0B111E] relative overflow-hidden"
      data-testid="better-roof-value-section"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#38BDF8]/5 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-[#38BDF8] text-sm font-medium mb-6"
          >
            <Award className="w-4 h-4" />
            Why Choose Us
          </motion.span>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Better Roof{' '}
            <span className="text-[#38BDF8]">=</span>{' '}
            <span className="text-[#F95738]">Better Value</span>
          </h2>
          
          <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
            Transform your home's appearance and protection with our professional roof painting services
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="glass rounded-2xl p-6 group cursor-pointer relative overflow-hidden"
            >
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ 
                  background: `radial-gradient(circle at 50% 0%, ${benefit.color}20, transparent 70%)`
                }}
              />
              
              <div className="relative z-10">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${benefit.color}20` }}
                >
                  <benefit.icon className="w-7 h-7" style={{ color: benefit.color }} />
                </div>
                
                <div 
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{ color: benefit.color }}
                >
                  {benefit.value}
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                
                <p className="text-sm text-[#94A3B8] leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Features List */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="glass rounded-2xl p-8 md:p-12"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Our Comprehensive Process
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
                    <span className="text-[#94A3B8]">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="relative rounded-xl overflow-hidden aspect-video"
              >
                <img
                  src="https://images.unsplash.com/photo-1632863640877-f15c81def7c5?w=800&q=80"
                  alt="Roof painting in progress"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B111E] via-transparent to-transparent" />
                
                {/* Floating stat */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.9 }}
                  className="absolute bottom-4 left-4 glass px-4 py-3 rounded-xl"
                >
                  <div className="text-2xl font-bold text-[#F95738]">1,500+</div>
                  <div className="text-xs text-[#94A3B8]">Roofs Restored</div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
